'use client';

/**
 * Investor Pitch Landing Page
 *
 * A hidden, shareable landing page for investor pitches
 * No navigation, no buttons - just pure information for potential investors
 * Access: Direct URL only (no links from main site)
 */

import { useEffect, useState } from 'react';
import {
  TrendingUp,
  Users,
  Target,
  Zap,
  Shield,
  DollarSign,
  CheckCircle2,
  ArrowRight,
  BarChart3,
  Award,
  Rocket,
  Building2,
  Globe,
  Lock,
  Gauge,
  LineChart,
  Brain,
  Network,
  Lightbulb,
  Star,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

export default function InvestorPitchPage() {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalScroll) * 100;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
        <div
          className="h-full bg-gradient-to-r from-purple-600 to-violet-600 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-4 sm:px-6 lg:px-12 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-0 w-full h-full opacity-30"
            style={{
              background: `
                radial-gradient(ellipse 80% 50% at 50% 40%, rgba(147, 51, 234, 0.08) 0%, transparent 50%),
                radial-gradient(ellipse 60% 40% at 80% 20%, rgba(251, 191, 36, 0.05) 0%, transparent 40%)
              `,
            }}
          />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-purple-50 border border-purple-100">
            <Lock className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-semibold text-purple-700">
              Confidential Investor Brief
            </span>
          </div>

          {/* Main Headline */}
          <h1
            className="font-display font-bold text-gray-900 leading-tight mb-6"
            style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}
          >
            The Smart Way to Match
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-violet-600">
              Investors with Verified Startups
            </span>
          </h1>

          <p className="text-xl lg:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            A matchmaking platform that connects qualified investors with
            fundable startups, supported by essential business toolkits and
            expert research services.
          </p>

          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <p className="text-3xl font-display font-bold text-purple-600">
                $100,000
              </p>
              <p className="text-sm text-gray-600 mt-1">Fundraising Target</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <p className="text-3xl font-display font-bold text-purple-600">
                3%
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Success Fee Per Match
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <p className="text-3xl font-display font-bold text-purple-600">
                $100
              </p>
              <p className="text-sm text-gray-600 mt-1">Per Service Module</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <p className="text-3xl font-display font-bold text-purple-600">
                $200K+
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Year 1 Revenue Target
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-red-50 text-red-700 border-red-200">
              The Problem
            </Badge>
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
              The Investor-Startup Matching Problem
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Investors waste time on unprepared startups. Founders lack the
              tools to become investor-ready. The matchmaking process is broken.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-red-200 bg-red-50/30">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Founders Struggle to Execute
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2" />
                    <span>Don&apos;t know where to start after ideation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2" />
                    <span>
                      Can&apos;t afford quality vendors or consultants
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2" />
                    <span>Vendor chaos with unpredictable pricing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2" />
                    <span>No structured framework for growth</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-amber-200 bg-amber-50/30">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Investors Lack Quality Deal Flow
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2" />
                    <span>90% of pitch decks are incomplete or unclear</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2" />
                    <span>
                      No objective fundability filter - just founder
                      self-reports
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2" />
                    <span>Time wasted on unprepared startups</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2" />
                    <span>
                      Need for due diligence research slows down decisions
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2" />
                    <span>
                      Hard to identify which startups are truly investment-ready
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Solution Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-green-50 text-green-700 border-green-200">
              Our Solution
            </Badge>
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
              Purple Venture: Smart Matchmaking + Business Toolkit
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We connect investors with fundable startups through objective
              scoring, while helping founders become investor-ready with
              essential business services and expert support.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-purple-200 bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    For Founders
                  </h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Essential Business Services Toolkit
                      </p>
                      <p className="text-sm text-gray-600">
                        Company registration, website, MVP, certifications -
                        $100 per module
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Expert Consulting Support
                      </p>
                      <p className="text-sm text-gray-600">
                        Access vetted consultants for specific business
                        challenges
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Fundability Score System
                      </p>
                      <p className="text-sm text-gray-600">
                        Objective scoring increases visibility to qualified
                        investors
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Direct Investor Matching
                      </p>
                      <p className="text-sm text-gray-600">
                        Get matched with investors looking for your sector/stage
                      </p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-violet-200 bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-violet-600 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    For Investors
                  </h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Pre-Vetted Deal Flow
                      </p>
                      <p className="text-sm text-gray-600">
                        Only startups with fundability score 50+ are visible to
                        you
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Research-on-Demand
                      </p>
                      <p className="text-sm text-gray-600">
                        Order custom research reports on startups or market
                        segments
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Investment Thesis Alignment
                      </p>
                      <p className="text-sm text-gray-600">
                        Get matched with startups that fit your sector/stage
                        focus
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Activity Signal Board
                      </p>
                      <p className="text-sm text-gray-600">
                        See real-time startup progress and execution metrics
                      </p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="border-2 border-gray-200 bg-gray-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Support Infrastructure
                  </h3>
                  <Badge variant="outline" className="mt-1">
                    Internal/Contractor Network
                  </Badge>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                We maintain a curated network of consultants and analysts to
                deliver services and research - they are not separate
                marketplace participants, but rather part of our service
                delivery mechanism.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Award className="w-4 h-4 text-indigo-600" />
                    Expert Consultants (Robin Hoods)
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2" />
                      <span>Deliver essential services toolkit modules</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2" />
                      <span>Solve specific founder challenges on-demand</span>
                    </li>
                  </ul>
                </div>
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Brain className="w-4 h-4 text-cyan-600" />
                    Research Analysts (Zero Associates)
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-2" />
                      <span>Conduct startup due diligence for investors</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-2" />
                      <span>Provide market and sector research reports</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Business Model Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-purple-50 text-purple-700 border-purple-200">
              Business Model
            </Badge>
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
              Three Core Revenue Streams
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Capital-efficient model with diversified revenue from matchmaking,
              services, and premium access
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="border-2 border-purple-200 hover:border-purple-400 transition-colors bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">
                  Matchmaking Success Fees
                </h3>
                <p className="text-3xl font-display font-bold text-purple-600 mb-2">
                  3%
                </p>
                <p className="text-sm text-gray-600 mb-3">
                  Commission per successful investor-startup match
                </p>
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Primary revenue driver - low overhead, high margin
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-violet-200 hover:border-violet-400 transition-colors bg-gradient-to-br from-violet-50 to-white">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center mb-4">
                  <Network className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">
                  Essential Services Toolkit
                </h3>
                <p className="text-3xl font-display font-bold text-violet-600 mb-2">
                  $100
                </p>
                <p className="text-sm text-gray-600 mb-3">
                  Per service module (registration, MVP, etc.) with 30% margin
                </p>
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Stable recurring revenue - helps startups become fundable
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-indigo-200 hover:border-indigo-400 transition-colors bg-gradient-to-br from-indigo-50 to-white">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <Star className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">
                  Investor Premium Access
                </h3>
                <p className="text-3xl font-display font-bold text-indigo-600 mb-2">
                  Subscription
                </p>
                <p className="text-sm text-gray-600 mb-3">
                  Enhanced access to deal flow + research reports on-demand
                </p>
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    High-value segment - builds investor stickiness
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-2 border-purple-200 bg-purple-50/30">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                Realistic Year 1 Revenue Projections (Milestone-Based)
              </h3>

              {/* Quarterly Revenue Build */}
              <div className="bg-white rounded-lg p-6 mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4 text-center">
                  Quarterly Revenue Ramp
                </h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <div>
                      <p className="font-semibold text-gray-900">
                        Q1 (Month 1-3): Services Launch
                      </p>
                      <p className="text-sm text-gray-600">
                        50 founders × 2 services avg × $50 early-bird pricing
                      </p>
                    </div>
                    <p className="text-2xl font-display font-bold text-purple-600">
                      $5,000
                    </p>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <div>
                      <p className="font-semibold text-gray-900">
                        Q2 (Month 4-6): First Matches
                      </p>
                      <p className="text-sm text-gray-600">
                        Services: $10K + 5 pilot matches × $300K avg × 3% = $45K
                      </p>
                    </div>
                    <p className="text-2xl font-display font-bold text-purple-600">
                      $55,000
                    </p>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <div>
                      <p className="font-semibold text-gray-900">
                        Q3 (Month 7-9): Scale Begins
                      </p>
                      <p className="text-sm text-gray-600">
                        Services: $15K + 12 matches × $350K avg × 3% +
                        Consulting: $8K
                      </p>
                    </div>
                    <p className="text-2xl font-display font-bold text-purple-600">
                      $149,000
                    </p>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <div>
                      <p className="font-semibold text-gray-900">
                        Q4 (Month 10-12): Momentum
                      </p>
                      <p className="text-sm text-gray-600">
                        Services: $20K + 20 matches × $400K avg × 3% +
                        Consulting: $15K + Premium subs: $5K
                      </p>
                    </div>
                    <p className="text-2xl font-display font-bold text-purple-600">
                      $280,000
                    </p>
                  </div>
                  <div className="flex justify-between items-center pt-3 bg-green-50 -mx-6 px-6 py-4 rounded-b-lg">
                    <div>
                      <p className="font-bold text-gray-900 text-lg">
                        Total Year 1 Revenue (Conservative)
                      </p>
                      <p className="text-sm text-gray-600">
                        37 total matches + services + consulting
                      </p>
                    </div>
                    <p className="text-3xl font-display font-bold text-green-600">
                      $489,000
                    </p>
                  </div>
                </div>
              </div>

              {/* Key Assumptions */}
              <div className="bg-white rounded-lg p-6 mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">
                  Key Assumptions (Conservative)
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2 text-sm">
                      Growth Metrics
                    </h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                        <span>50 founders by M3, 200 by M12</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                        <span>15 investors by M6, 50 by M12</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                        <span>Match success rate: 10-15% in Year 1</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                        <span>Avg deal size: $300K-400K range</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2 text-sm">
                      Revenue Mix
                    </h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2" />
                        <span>Matchmaking fees: ~73% of revenue</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2" />
                        <span>Services toolkit: ~10% of revenue</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2" />
                        <span>Consulting: ~9% of revenue</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2" />
                        <span>Premium subs (Q4 only): ~1% of revenue</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Additional Context */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h5 className="font-semibold text-gray-900 mb-2">
                    Growth Multipliers
                  </h5>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                      <span>
                        Additional premium subscriptions for investors
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                      <span>Research outsourcing to Zero Associates</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                      <span>Data products and market intelligence</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h5 className="font-semibold text-gray-900 mb-2">
                    Unit Economics Health
                  </h5>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2" />
                      <span>
                        Low customer acquisition cost via network effects
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2" />
                      <span>
                        High margins on success fees (minimal ops cost)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2" />
                      <span>Repeat revenue from ongoing engagements</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Tech-Driven ROI Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-50 text-purple-700 border-purple-200">
              Technology Architecture
            </Badge>
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
              AI-Powered Matchmaking Engine + SaaS Revenue Model
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Scalable technology platform generating revenue from Day 1 with
              compounding network effects
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Core Technology Stack */}
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Core Technology
                    </h3>
                    <Badge variant="outline" className="mt-1">
                      Automated &amp; Scalable
                    </Badge>
                  </div>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        AI Fundability Scoring Algorithm
                      </p>
                      <p className="text-sm text-gray-600">
                        Automated startup evaluation reduces manual screening by
                        80%
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Smart Matching Engine
                      </p>
                      <p className="text-sm text-gray-600">
                        ML-powered investor-startup pairing based on thesis,
                        sector, and stage
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Automated Workflow Platform
                      </p>
                      <p className="text-sm text-gray-600">
                        Service delivery, milestone tracking, payment automation
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Data Intelligence Layer
                      </p>
                      <p className="text-sm text-gray-600">
                        Every transaction builds proprietary market intelligence
                      </p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Quick ROI Path */}
            <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Fast Path to ROI
                    </h3>
                    <Badge
                      variant="outline"
                      className="mt-1 border-green-600 text-green-700"
                    >
                      Revenue from Month 1
                    </Badge>
                  </div>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        SaaS Revenue - Immediate
                      </p>
                      <p className="text-sm text-gray-600">
                        $100/service module with 30% margin from Day 1
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Transaction Fees - Month 3-6
                      </p>
                      <p className="text-sm text-gray-600">
                        3% success fees on investor-startup matches (high
                        margin)
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Premium Subscriptions - Month 6+
                      </p>
                      <p className="text-sm text-gray-600">
                        Investor premium access ($299-499/month recurring)
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Data Products - Year 2
                      </p>
                      <p className="text-sm text-gray-600">
                        Market intelligence reports and API access
                      </p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Scalability & Network Effects */}
          <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="pt-8 pb-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Technology-Driven Scalability
                </h3>
                <p className="text-gray-600">
                  Automated platform operations reduce marginal cost per
                  transaction to near-zero
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-white rounded-lg border border-blue-200">
                  <Gauge className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-900 mb-2">
                    80% Automation
                  </h4>
                  <p className="text-sm text-gray-600">
                    Matching, scoring, and workflow automation reduces ops costs
                  </p>
                </div>
                <div className="text-center p-6 bg-white rounded-lg border border-blue-200">
                  <Network className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-900 mb-2">
                    Network Effects
                  </h4>
                  <p className="text-sm text-gray-600">
                    Each new user increases platform value for all participants
                  </p>
                </div>
                <div className="text-center p-6 bg-white rounded-lg border border-blue-200">
                  <LineChart className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-900 mb-2">
                    70%+ Gross Margin
                  </h4>
                  <p className="text-sm text-gray-600">
                    Software margins on success fees and subscriptions
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-blue-200">
                <h4 className="font-semibold text-gray-900 mb-4 text-center">
                  Revenue Ramp Timeline
                </h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold text-sm">M1</span>
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">
                      Services Live
                    </p>
                    <p className="text-xs text-gray-600">
                      $5K+ MRR from toolkit
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold text-sm">M3</span>
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">
                      First Matches
                    </p>
                    <p className="text-xs text-gray-600">
                      Success fees kick in
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold text-sm">M6</span>
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">
                      Premium Launch
                    </p>
                    <p className="text-xs text-gray-600">$15K+ MRR total</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold text-sm">M12</span>
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">
                      PMF + Scale
                    </p>
                    <p className="text-xs text-gray-600">$100K+ ARR target</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Investment Advantages */}
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
              <CardContent className="pt-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-indigo-600" />
                  Why Investors See Quick Returns
                </h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2" />
                    <span>
                      Multiple revenue streams reduce risk, accelerate
                      profitability
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2" />
                    <span>
                      Platform model with software margins scales efficiently
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2" />
                    <span>
                      Network effects create defensible moat and pricing power
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2" />
                    <span>Low CAC through organic growth and viral loops</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-white">
              <CardContent className="pt-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Rocket className="w-5 h-5 text-violet-600" />
                  Technology Moat
                </h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2" />
                    <span>
                      Proprietary AI scoring gets smarter with every startup
                      evaluated
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2" />
                    <span>
                      Data moat builds with transaction history and outcomes
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2" />
                    <span>
                      High switching costs as users build reputation on platform
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2" />
                    <span>
                      First-mover advantage in structured execution marketplace
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Market Opportunity Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-blue-50 text-blue-700 border-blue-200">
              Market Opportunity
            </Badge>
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
              Massive Underserved Market
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              India&apos;s startup ecosystem is exploding, creating
              unprecedented demand for execution support
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="border-2 border-blue-200 bg-white text-center">
              <CardContent className="pt-6">
                <Globe className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <p className="text-4xl font-display font-bold text-gray-900 mb-2">
                  90K+
                </p>
                <p className="text-gray-600">
                  Recognized startups in India (DPIIT)
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-violet-200 bg-white text-center">
              <CardContent className="pt-6">
                <Rocket className="w-12 h-12 text-violet-600 mx-auto mb-4" />
                <p className="text-4xl font-display font-bold text-gray-900 mb-2">
                  $10B+
                </p>
                <p className="text-gray-600">Annual VC funding in India</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200 bg-white text-center">
              <CardContent className="pt-6">
                <Users className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <p className="text-4xl font-display font-bold text-gray-900 mb-2">
                  1.5M+
                </p>
                <p className="text-gray-600">
                  Potential Robin Hoods &amp; Zero Associates
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-2 border-gray-200 bg-white">
            <CardContent className="pt-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Target Addressable Market (TAM)
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700 font-medium">
                      Early-stage startups (Seed to Series A)
                    </span>
                    <span className="text-purple-600 font-bold">
                      ~40,000 active
                    </span>
                  </div>
                  <Progress value={75} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700 font-medium">
                      Avg. execution spend per startup (annual)
                    </span>
                    <span className="text-purple-600 font-bold">
                      $2,500-6,000
                    </span>
                  </div>
                  <Progress value={60} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700 font-medium">
                      Estimated TAM (conservative)
                    </span>
                    <span className="text-purple-600 font-bold">
                      $14M+ annually
                    </span>
                  </div>
                  <Progress value={90} className="h-2" />
                </div>
              </div>
              <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Platform capture potential:</strong> Even at 5% market
                  penetration with 3% success fee on matches, platform generates
                  strong recurring revenue from multiple streams (matchmaking
                  fees, services toolkit, and premium subscriptions)
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Competitive Advantage Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-green-50 text-green-700 border-green-200">
              Competitive Moat
            </Badge>
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
              Why We&apos;ll Win
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Network effects, proprietary data moat, and execution-first
              approach create sustainable advantages
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
              <CardContent className="pt-6">
                <Shield className="w-12 h-12 text-green-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Two-Sided Network Effect
                </h3>
                <p className="text-gray-600 mb-4">
                  More quality startups attract more investors → More investors
                  attract better startups → Flywheel accelerates as fundability
                  scores improve deal quality
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Classic marketplace network effects without complexity
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    High switching costs as both sides build reputation
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="pt-6">
                <Gauge className="w-12 h-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Proprietary Fundability Algorithm
                </h3>
                <p className="text-gray-600 mb-4">
                  Data-driven scoring system creates objective quality filter -
                  the more data we collect, the smarter it gets
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    Continuous learning from engagement outcomes
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    Investors trust platform vetting over self-reported metrics
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="pt-6">
                <Lightbulb className="w-12 h-12 text-purple-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Services-First Approach
                </h3>
                <p className="text-gray-600 mb-4">
                  Not just matchmaking - we help startups become fundable
                  through essential services, then connect them with investors
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-purple-600" />
                    Creates stickiness - startups build fundability on our
                    platform
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-purple-600" />
                    Investors trust our vetting process, not just self-reports
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
              <CardContent className="pt-6">
                <LineChart className="w-12 h-12 text-indigo-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Outcome Data Moat
                </h3>
                <p className="text-gray-600 mb-4">
                  Every engagement generates structured data - builds
                  intelligence layer competitors can&apos;t replicate
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                    Future data products and market intelligence offerings
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                    Early mover advantage in ecosystem intelligence
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Use of Funds Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-purple-50 text-purple-700 border-purple-200">
              Investment Ask
            </Badge>
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
              $100,000 Fundraise
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              12-18 month runway to reach Product-Market Fit and $100,000+ ARR
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="border-2 border-purple-200 bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                  <Badge className="bg-purple-100 text-purple-700">20%</Badge>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Team Hiring
                </h3>
                <p className="text-gray-600 mb-4">$20,000</p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                    <span>CTO / Lead Engineer (full-stack)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                    <span>Product Manager (platform experience)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                    <span>Operations Lead (community management)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                    <span>Sales/BD for investor onboarding</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-violet-200 bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Zap className="w-6 h-6 text-violet-600" />
                  </div>
                  <Badge className="bg-violet-100 text-violet-700">40%</Badge>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Technology
                </h3>
                <p className="text-gray-600 mb-4">$40,000</p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2" />
                    <span>Platform development (backend + frontend)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2" />
                    <span>Payment gateway &amp; escrow integration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2" />
                    <span>Fundability algorithm development</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2" />
                    <span>Infrastructure, security, compliance</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-indigo-200 bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-indigo-600" />
                  </div>
                  <Badge className="bg-indigo-100 text-indigo-700">40%</Badge>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Marketing &amp; Growth
                </h3>
                <p className="text-gray-600 mb-4">$40,000</p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2" />
                    <span>Founder/investor community building</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2" />
                    <span>Robin Hood recruitment campaigns</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2" />
                    <span>Content marketing &amp; thought leadership</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2" />
                    <span>Pilot partnerships with accelerators/VCs</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="border-2 border-purple-200 bg-purple-50/30">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                18-Month Milestones
              </h3>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white rounded-full border-4 border-purple-200 flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold text-purple-600">
                      3
                    </span>
                  </div>
                  <p className="font-semibold text-gray-900">Month 3</p>
                  <p className="text-sm text-gray-600">
                    Platform beta + 50 pilot users
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-white rounded-full border-4 border-purple-300 flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold text-purple-600">
                      6
                    </span>
                  </div>
                  <p className="font-semibold text-gray-900">Month 6</p>
                  <p className="text-sm text-gray-600">
                    200 founders, 50 Robin Hoods
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-white rounded-full border-4 border-purple-400 flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold text-purple-600">
                      12
                    </span>
                  </div>
                  <p className="font-semibold text-gray-900">Month 12</p>
                  <p className="text-sm text-gray-600">
                    500 active users + PMF
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-600 rounded-full border-4 border-purple-600 flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold text-white">18</span>
                  </div>
                  <p className="font-semibold text-gray-900">Month 18</p>
                  <p className="text-sm text-gray-600">
                    $100,000+ ARR, Series A ready
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-gray-100 text-gray-700 border-gray-200">
              The Team
            </Badge>
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
              Founding Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Solopreneur committed to solving a real problem in India&apos;s
              startup ecosystem
            </p>
          </div>

          <Card className="border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-white max-w-2xl mx-auto">
            <CardContent className="pt-8">
              <div className="text-center mb-6">
                <img
                  src="https://deepbuild-v1.s3.ap-south-1.amazonaws.com/uploads/1769768818008-4zxbnd50bhc-Nams_pic.jpeg"
                  alt="Namita Sharma"
                  className="w-32 h-32 rounded-full object-cover mx-auto mb-4 border-4 border-purple-100"
                />
                <h3 className="text-2xl font-bold text-gray-900">
                  Namita Sharma
                </h3>
                <p className="text-purple-600 font-semibold">
                  Founder &amp; CEO
                </p>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Domain Expertise
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-600" />
                      <span>Deep understanding of startup pain points</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-600" />
                      <span>Product &amp; technology background</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-600" />
                      <span>Network within India&apos;s startup ecosystem</span>
                    </li>
                  </ul>
                </div>

                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Why Now?</h4>
                  <p className="text-sm text-gray-600">
                    India&apos;s startup ecosystem is at an inflection point.
                    Post-pandemic, founders need execution support more than
                    ever, and investors need better signal in a noisy market.
                    The founding team identified this gap through direct
                    experience and is building the solution.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Cold Start Strategy Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-blue-50 text-blue-700 border-blue-200">
              Cold Start Strategy
            </Badge>
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
              Solving the Chicken-and-Egg Problem
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Step-by-step approach to acquire high-quality pilot users on both
              sides
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <Card className="border-2 border-green-200 bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Phase 1: Founders First (Month 1-3)
                  </h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-green-700">
                        1
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        Target Accelerator Cohorts
                      </p>
                      <p className="text-sm text-gray-600">
                        Partner with 3-4 accelerators (Y Combinator India,
                        Antler, etc.) - direct access to 30-40 pre-vetted
                        founders actively seeking execution support
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-green-700">
                        2
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        Services-First Onboarding
                      </p>
                      <p className="text-sm text-gray-600">
                        Offer discounted Essential Services Toolkit ($50 vs
                        $100) - solves immediate pain, no matchmaking
                        dependency. Revenue from Day 1.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-green-700">
                        3
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        Fundability Score Incentive
                      </p>
                      <p className="text-sm text-gray-600">
                        Free fundability assessment for early adopters - builds
                        database of scored startups before investors arrive
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-green-700">
                        4
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        Goal: 50 Quality Founders
                      </p>
                      <p className="text-sm text-gray-600">
                        Focus on fundability score 40+ startups in target
                        sectors (SaaS, D2C, EdTech, HealthTech)
                      </p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-violet-200 bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-violet-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Phase 2: Investors (Month 3-6)
                  </h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-violet-700">
                        1
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        Leverage Proof of Quality
                      </p>
                      <p className="text-sm text-gray-600">
                        Show 50+ scored, fundable startups already on platform -
                        solves investor&apos;s &quot;quality deal flow&quot;
                        problem immediately
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-violet-700">
                        2
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        Direct Outreach to Angel Networks
                      </p>
                      <p className="text-sm text-gray-600">
                        Target Indian Angel Network, LetsVenture, Mumbai Angels
                        - 15-20 active angels looking for pre-filtered deals
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-violet-700">
                        3
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        Free Research Reports Trial
                      </p>
                      <p className="text-sm text-gray-600">
                        Offer 1-2 free research reports on platform startups -
                        demonstrates value before asking for commitment
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-violet-700">
                        4
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        Goal: 15-20 Active Investors
                      </p>
                      <p className="text-sm text-gray-600">
                        Quality over quantity - focus on investors who actively
                        write checks ($50K-500K range)
                      </p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                Why This Solves Cold Start
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-white rounded-lg border border-purple-200">
                  <Award className="w-10 h-10 text-purple-600 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-900 mb-2">
                    Services = Standalone Value
                  </h4>
                  <p className="text-sm text-gray-600">
                    Founders get immediate value from toolkit even before
                    matchmaking launches - no dependency on investor supply
                  </p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border border-purple-200">
                  <Network className="w-10 h-10 text-purple-600 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-900 mb-2">
                    Proof Before Pitch
                  </h4>
                  <p className="text-sm text-gray-600">
                    By Month 3, we show investors 50+ scored startups - credible
                    promise of quality, not just a pitch deck
                  </p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border border-purple-200">
                  <Target className="w-10 h-10 text-purple-600 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-900 mb-2">
                    Accelerator Partnerships
                  </h4>
                  <p className="text-sm text-gray-600">
                    Direct channel to motivated, execution-focused founders who
                    are actively fundraising - perfect ICP
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Traction & Proof Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-orange-50 text-orange-700 border-orange-200">
              Current Status
            </Badge>
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
              Product Development Stage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Full-featured frontend built and deployed - ready for backend
              development and pilot launch
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-green-200 bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    What&apos;s Built
                  </h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Complete Frontend
                      </p>
                      <p className="text-sm text-gray-600">
                        All user flows, dashboards, and pages designed and coded
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        4 Stakeholder Journeys
                      </p>
                      <p className="text-sm text-gray-600">
                        Founder, Investor, Robin Hood, Zero Associate flows
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Essential Services Toolkit
                      </p>
                      <p className="text-sm text-gray-600">
                        7 service modules designed with intake flows
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Fundability Framework
                      </p>
                      <p className="text-sm text-gray-600">
                        Scoring methodology and dashboard UI ready
                      </p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200 bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Next Steps
                  </h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Backend Development
                      </p>
                      <p className="text-sm text-gray-600">
                        API, database, payment integration, auth system
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Pilot User Recruitment
                      </p>
                      <p className="text-sm text-gray-600">
                        50 founders, 20 Robin Hoods for beta testing
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Team Expansion
                      </p>
                      <p className="text-sm text-gray-600">
                        Hire CTO, Product, Operations, Sales roles
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Partnership Development
                      </p>
                      <p className="text-sm text-gray-600">
                        Pilot with 2-3 accelerators/VC firms
                      </p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-6">
                <Zap className="w-8 h-8 text-purple-600" />
                <h3 className="text-2xl font-bold text-gray-900">
                  Why This is the Right Time to Invest
                </h3>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Product Clarity
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Full frontend de-risks product design. We know exactly what
                    to build on the backend. No ambiguity, clear execution path.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Market Validation
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Founder pain points validated through direct conversations.
                    Investor interest confirmed through ecosystem discussions.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Timing Advantage
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Post-pandemic execution gap is real and growing. Early mover
                    advantage in structured execution marketplace.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Capital Efficient
                  </h4>
                  <p className="text-gray-600 text-sm">
                    $100,000 gets us to PMF and profitability. Network effects
                    kick in early. Clear path to Series A.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Investment Terms Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-purple-50 text-purple-700 border-purple-200">
              Investment Details
            </Badge>
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
              Simple, Founder-Friendly Terms
            </h2>
          </div>

          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="pt-8 pb-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Investment Structure
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Amount Raising</span>
                      <span className="font-semibold text-gray-900">
                        $100,000
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Stage</span>
                      <span className="font-semibold text-gray-900">
                        Pre-Seed
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Instrument</span>
                      <span className="font-semibold text-gray-900">
                        SAFE / Equity
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Valuation Cap</span>
                      <span className="font-semibold text-gray-900">
                        To be discussed
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    What You Get
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">
                          Early mover advantage
                        </p>
                        <p className="text-sm text-gray-600">
                          Ground floor entry into high-growth platform
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">
                          Clear execution path
                        </p>
                        <p className="text-sm text-gray-600">
                          Product designed, market validated, team focused
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">
                          Quarterly updates
                        </p>
                        <p className="text-sm text-gray-600">
                          Transparent reporting on metrics and milestones
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">
                          Network effects potential
                        </p>
                        <p className="text-sm text-gray-600">
                          4-sided marketplace with compounding growth
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200 text-center">
                <p className="text-gray-600 mb-2">
                  Open to angel investors, micro-VCs, and strategic partners
                </p>
                <p className="text-sm text-gray-500">
                  Ideal investor: Understands marketplace dynamics, can open
                  doors in startup ecosystem
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-purple-900 to-violet-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-6">
            Join Us in Building India&apos;s Startup Infrastructure
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            This is not just a platform - it&apos;s the foundational layer for
            how startups execute and how investors discover quality deals in
            India.
          </p>

          <Card className="border-2 border-purple-300 bg-white/10 backdrop-blur-sm text-white">
            <CardContent className="pt-8 pb-8">
              <h3 className="text-2xl font-bold mb-6">Next Steps</h3>
              <div className="grid md:grid-cols-3 gap-6 text-left">
                <div>
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                    <span className="text-2xl font-bold">1</span>
                  </div>
                  <h4 className="font-semibold mb-2">Review Deck</h4>
                  <p className="text-sm text-purple-100">
                    Take time to understand the model and opportunity
                  </p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                    <span className="text-2xl font-bold">2</span>
                  </div>
                  <h4 className="font-semibold mb-2">Schedule Call</h4>
                  <p className="text-sm text-purple-100">
                    30-minute deep dive with founding team
                  </p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                    <span className="text-2xl font-bold">3</span>
                  </div>
                  <h4 className="font-semibold mb-2">Due Diligence</h4>
                  <p className="text-sm text-purple-100">
                    Access full financials, product demo, team background
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mt-12 p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
            <p className="text-lg mb-2">
              <strong>Contact:</strong> Confidential investor brief - reach out
              via the channel you received this link
            </p>
            <p className="text-sm text-purple-200">
              We&apos;re selective about our investors. Looking for partners who
              understand platform businesses and can add strategic value beyond
              capital.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-900 text-gray-400 text-center text-sm">
        <p className="mb-2">Purple Venture - Results-as-a-Service Platform</p>
        <p className="text-xs">
          © 2026 Purple Venture. Confidential investor materials. Do not
          distribute without permission.
        </p>
      </footer>
    </div>
  );
}
